import React, { useEffect, useState } from "react";
import axios from "axios";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { LineChart as ChartIcon, Loader2 } from "lucide-react";
import { API } from "../../../App";
import { getAuthCfg, Panel, InlineError, money } from "./singleUtils";

const SERIES_COLORS = { Collected: "#10b981", Expenses: "#e11d48", Commissions: "#4f46e5", "Operating Result": "#f59e0b" };

// Compare collected vs expenses vs commissions vs operating result over time.
const FinChart = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchChart = async () => {
    setLoading(true); setError(false);
    try {
      const res = await axios.get(`${API}/store/dashboard-v2/chart`, { ...getAuthCfg(), params: { metric: "finance", days } });
      setData(res.data);
    } catch { setError(true); } finally { setLoading(false); }
  };
  useEffect(() => { fetchChart(); /* eslint-disable-next-line */ }, [days]);

  const rows = (data?.labels || []).map((day, i) => {
    const row = { day: day.slice(5) };
    (data?.series || []).forEach((s) => { row[s.name] = s.data[i]; });
    return row;
  });
  const names = (data?.series || []).map((s) => s.name);

  return (
    <Panel title="Performance Chart" icon={ChartIcon} testid="finance-chart"
      right={
        <div className="flex rounded-sm border border-border overflow-hidden">
          {[7, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-2 py-0.5 text-[10px] font-bold font-mono ${days === d ? "bg-amber-500 text-black" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`fin-chart-days-${d}`}>{d}D</button>
          ))}
        </div>
      }>
      {error ? (
        <InlineError label="chart data" onRetry={fetchChart} />
      ) : loading && !data ? (
        <div className="h-48 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>
      ) : (
        <div className="h-48" data-testid="fin-chart-area">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={rows} margin={{ top: 6, right: 4, left: 0, bottom: 0 }}>
              <defs>
                {names.map((n) => (
                  <linearGradient key={n} id={`fg-${n.replace(/\s/g, "")}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={SERIES_COLORS[n] || "#64748b"} stopOpacity={0.18} />
                    <stop offset="100%" stopColor={SERIES_COLORS[n] || "#64748b"} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="2 4" stroke="currentColor" opacity={0.08} />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={44}
                tickFormatter={(v) => `$${Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`} />
              <Tooltip formatter={(v, name) => [money(v), name]} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {names.map((n) => (
                <Area key={n} type="monotone" dataKey={n} stroke={SERIES_COLORS[n] || "#64748b"}
                  strokeWidth={n === "Operating Result" ? 2.5 : 1.5} fill={`url(#fg-${n.replace(/\s/g, "")})`} dot={false} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
};

export default FinChart;
